import {useCallback, useSyncExternalStore} from 'react';
import type {Component, Editor} from 'grapesjs';
import {
  AUTO_WIDTH_CTA_CLASS,
  BACKGROUND_COLOR_STYLE_PROPERTY,
  BGCOLOR_ATTRIBUTE,
  BUTTON_WIDTH_AUTO,
  BUTTON_WIDTH_CUSTOM,
  BUTTON_WIDTH_STYLE_PROP,
  EMAIL_MAKER_COMPONENT_TYPES,
  EMAIL_MAKER_STYLES,
  MAX_CONTENT_WIDTH,
  STYLE_UPDATE_EVENT_PREFIX,
} from '@EmailMakerReact/email-maker-react.constants';
import type {StyleAttributes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {isComponentWidthUnset, isOfTypeCell, isOfTypeGraphic} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {unwrapElement} from '@EmailMakerReact/lib/email-maker-rte-content-normalize';
import {forEachResponsiveDevice, setDeviceMediaQuery} from '@EmailMakerReact/lib/email-maker-media-query-library';
import {clearDividerCompatibilityProperty, updateDividerCompatibilityProperty} from '@EmailMakerReact/lib/email-maker-table-library';

/** Style setters, text styling, alignment, and width handling for the React email maker. */

export type HorizontalAlignment = 'left' | 'center' | 'right';
export type VerticalAlignment = 'top' | 'middle' | 'bottom';
export type TextAlignment = 'left' | 'center' | 'right' | 'justify';
export type StyleChangeSubscribe = (onChange: () => void) => () => void;

interface HorizontalMargins {
  left: string;
  right: string;
}

export interface ComponentStyleSetterOptions {
  /** Falls back to the current selection when omitted. */
  component?: Component;
  property: string;
  value: string;
}

export interface ToggleComponentStyleOptions {
  property: string;
  activeValue: string;
  defaultValue: string;
}

/** Sets one style property, mirroring it for dividers, link colors, and styleUpdate listeners. */
export function setComponentStyle(editor: Editor, options: ComponentStyleSetterOptions): void {
  const component = options.component ?? editor.getSelected();

  if (!component) {
    return;
  }

  updateDividerCompatibilityProperty(editor, {
    component,
    property: options.property,
    value: options.value,
  });

  if (options.property === EMAIL_MAKER_STYLES.LINK_COLOR) {
    const attributesToAdd: StyleAttributes = {};
    attributesToAdd[EMAIL_MAKER_STYLES.LINK_COLOR] = options.value;
    component.addAttributes(attributesToAdd);
  }

  const style: StyleAttributes = {};
  style[options.property] = options.value;
  component.addStyle(style);
  editor.trigger(`${STYLE_UPDATE_EVENT_PREFIX}${options.property}`, component);
}

/** Sets the text color after stripping RTE-colorpicker font tags. */
export function setTextColorStyle(editor: Editor, value: string, component?: Component): void {
  const target = component ?? editor.getSelected();

  if (!target) {
    return;
  }

  removeRteColorpickerFontTagsIfNeeded(target);

  const property = 'color';
  const style: StyleAttributes = {[property]: value};
  target.addStyle(style);
  editor.trigger(`${STYLE_UPDATE_EVENT_PREFIX}${property}`, target);
}

/** Flips the selected component's style property between the active and default values. */
export function toggleComponentStyle(editor: Editor, options: ToggleComponentStyleOptions): void {
  const component = editor.getSelected();

  if (!component) {
    return;
  }

  const currentValue = component.getStyle()[options.property];

  if (typeof currentValue === 'string' && currentValue === options.activeValue) {
    setComponentStyle(editor, {property: options.property, value: options.defaultValue});
  } else {
    setComponentStyle(editor, {property: options.property, value: options.activeValue});
  }
}

/** Toggles bold on the selection. */
export function toggleBold(editor: Editor): void {
  toggleComponentStyle(editor, {
    property: 'font-weight',
    activeValue: 'bold',
    defaultValue: 'normal',
  });
}

/** Toggles italic on the selection. */
export function toggleItalic(editor: Editor): void {
  toggleComponentStyle(editor, {
    property: 'font-style',
    activeValue: 'italic',
    defaultValue: 'normal',
  });
}

/** Toggles underline on the selection. */
export function toggleUnderline(editor: Editor): void {
  toggleTextDecorationStyle(editor, 'underline');
}

/** Toggles strikethrough on the selection. */
export function toggleStrikethrough(editor: Editor): void {
  toggleTextDecorationStyle(editor, 'line-through');
}

/** Sets text-align on the selected text component. */
export function alignSelectedText(editor: Editor, alignment: TextAlignment): void {
  const selected = editor.getSelected();
  if (selected && componentHasText(selected)) {
    setComponentStyle(editor, {property: 'text-align', value: alignment});
  }
}

/** Vertical-aligns the selected cell. */
export function setVerticalAlignment(editor: Editor, value: VerticalAlignment): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  const styles = component.getStyle();
  styles['vertical-align'] = value;
  component.setStyle(styles);
  editor.trigger(`${STYLE_UPDATE_EVENT_PREFIX}vertical-align`, component);
}

/** Horizontal-aligns the selected component via margins + align attribute. */
export function setHorizontalAlignment(editor: Editor, value: HorizontalAlignment): void {
  const component = editor.getSelected();
  if (!component) {
    return;
  }

  const styles = component.getStyle();

  if (value === 'center') {
    styles['margin-left'] = 'auto';
    styles['margin-right'] = 'auto';
  } else if (value === 'left') {
    styles['margin-left'] = '0';
    styles['margin-right'] = 'auto';
  } else {
    styles['margin-left'] = 'auto';
    styles['margin-right'] = '0';
  }

  component.setStyle(styles);
  addAlignAttribute(editor, value, component);
  editor.trigger(`${STYLE_UPDATE_EVENT_PREFIX}margin`, component);
}

export function getHorizontalAlignment(component: Component): HorizontalAlignment | '' {
  const {left, right} = getHorizontalMargins(component);
  if (left === 'auto' && right === 'auto') {
    return 'center';
  }
  if (left === 'auto' && isZeroMargin(right)) {
    return 'right';
  }
  if (right === 'auto' && isZeroMargin(left)) {
    return 'left';
  }
  return '';
}

function isZeroMargin(margin: string): boolean {
  return parseInt(margin, 10) === 0;
}

function getHorizontalMargins(component: Component): HorizontalMargins {
  const shorthand = expandMarginShorthand(getComponentStyleProperty(component, 'margin'));
  return {
    left: getComponentStyleProperty(component, 'margin-left') || shorthand.left,
    right: getComponentStyleProperty(component, 'margin-right') || shorthand.right,
  };
}

function expandMarginShorthand(margin: string): HorizontalMargins {
  const tokens = margin.trim().split(/\s+/).filter(Boolean);
  switch (tokens.length) {
    case 1:
      return {left: tokens[0], right: tokens[0]};
    case 2:
    case 3:
      return {left: tokens[1], right: tokens[1]};
    case 4:
      return {left: tokens[3], right: tokens[1]};
    default:
      return {left: '', right: ''};
  }
}

/** Writes the HTML align attribute onto a graphic's parent cell for email-client compatibility. */
export function addAlignAttribute(editor: Editor, alignment: HorizontalAlignment, component?: Component): void {
  const target = component ?? editor.getSelected();
  if (!target) {
    return;
  }

  if (!isOfTypeGraphic(editor, target)) {
    return;
  }

  const parent = target.parent();
  if (parent && isOfTypeCell(editor, parent)) {
    parent.addAttributes({
      align: alignment,
    });
  }
}

/** Caps the content component's width to the canvas max. */
export function updateContentMaxWidth(editor: Editor, component: Component | undefined): void {
  if (!component) {
    return;
  }

  setComponentStyle(editor, {property: 'max-width', value: MAX_CONTENT_WIDTH, component});
}

/** Applies a custom percentage width to the component. */
export function setCustomWidthStyle(editor: Editor, component: Component | undefined, widthValue: string): void {
  if (!component || !widthValue) {
    return;
  }

  component.removeClass(AUTO_WIDTH_CTA_CLASS);

  setComponentStyle(editor, {property: 'width', value: `${widthValue}%`, component});
  setComponentStyle(editor, {property: BUTTON_WIDTH_STYLE_PROP, value: BUTTON_WIDTH_CUSTOM, component});

  let display = 'block';
  let minWidth = 'fit-content';

  if (component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON) && !isComponentWidthUnset(editor, component)) {
    display = 'table';
    minWidth = 'auto';
  }

  setComponentStyle(editor, {property: 'display', value: display, component});
  forEachResponsiveDevice((deviceID, cssPrefix) => {
    setDeviceMediaQuery(editor, {property: 'min-width', value: minWidth, id: component.ccid, deviceID, cssPrefix});
  });
}

/** Applies auto/fit-content width to the component (and a button's inner cell). */
export function setAutoWidthStyle(editor: Editor, component: Component | undefined): void {
  if (!component) {
    return;
  }

  component.addClass(AUTO_WIDTH_CTA_CLASS);

  let width = 'fit-content';
  if (component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON)) {
    width = 'auto';
    const innerTableCell = component.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL}"]`)[0];

    if (innerTableCell) {
      setComponentStyle(editor, {property: 'display', value: 'table', component});
      setComponentStyle(editor, {property: 'width', value: width, component: innerTableCell});
    }
  }

  setComponentStyle(editor, {property: 'width', value: width, component});
  setComponentStyle(editor, {property: BUTTON_WIDTH_STYLE_PROP, value: BUTTON_WIDTH_AUTO, component});
  forEachResponsiveDevice((deviceID, cssPrefix) => {
    setDeviceMediaQuery(editor, {property: 'width', value: width, id: component.ccid, deviceID, cssPrefix});
  });
}

/** Re-applies the text component's pmw-link-color to its links. */
export function updateTextComponentLinkColor(editor: Editor, component?: Component): void {
  let target = component ?? editor.getSelected();

  if (!target) {
    return;
  }

  let color: string | undefined;

  if (target.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT)) {
    color = getTextLinkColor(target);
  }

  if (target.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK)) {
    const parent = target.parent();
    if (parent?.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT)) {
      color = getTextLinkColor(parent);
    }

    target = parent;
  }

  if (!color) {
    return;
  }

  updateLinksColor(target, color);
}

/** Sets the color of every link component inside the component. */
export function updateLinksColor(component: Component | undefined, color: string): void {
  if (!component) {
    return;
  }

  component.find(`[data-gjs-type="${EMAIL_MAKER_COMPONENT_TYPES.text.PMW_LINK}"]`).forEach((linkComponent) => {
    const style = linkComponent.getStyle();
    style.color = color;
    linkComponent.setStyle(style);
  });
}

/** Toggles one token inside the text-decoration value list. */
function toggleTextDecorationStyle(editor: Editor, styleToToggle: string): void {
  const selected = editor.getSelected();

  if (!selected) {
    return;
  }

  if (!(selected.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT) || selected.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON))) {
    return;
  }

  const styles = selected.getStyle();
  const textDecoration = typeof styles['text-decoration'] === 'string' ? styles['text-decoration'] : '';
  const currentStyles = textDecoration.split(' ');

  const newStyles = currentStyles.filter((style) => {
    return style !== styleToToggle && style !== 'none';
  });

  if (!currentStyles.includes(styleToToggle)) {
    newStyles.push(styleToToggle);
  }

  if (newStyles.length === 0) {
    newStyles.push('none');
  }

  setComponentStyle(editor, {property: 'text-decoration', value: newStyles.join(' ')});
}

/** Whether the component contains editable text. */
function componentHasText(component: Component): boolean {
  return (
    component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT) ||
    component.is(EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON) ||
    component.is(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS)
  );
}

/** Unwraps &lt;font&gt; tags the RTE colorpicker leaves behind so the component color applies uniformly. */
function removeRteColorpickerFontTagsIfNeeded(component: Component): void {
  const el = component.getEl();

  if (!el) {
    return;
  }

  const elCopy = document.createElement('div');
  elCopy.innerHTML = el.innerHTML;

  // The RTE colorpicker emits deprecated <font> tags; querying them is the point of this cleanup.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const fontTags = elCopy.querySelectorAll('font');

  if (fontTags.length === 0) {
    return;
  }

  fontTags.forEach((fontTag) => {
    unwrapElement(fontTag);
  });

  component.components(elCopy.innerHTML);
}

/** Reads one CSS property from a component's style map. Returns '' when unset. */
export function getComponentStyleProperty(component: Component, property: string): string {
  const style = component.getStyle() as Record<string, string | undefined>;
  return style[property] ?? '';
}

/**
 * Drops the redundant inline `style` attribute that loaded templates carry in their RAW attributes.
 * Callers that also write a non-style attribute on the same component must call this first, because
 * GrapesJS `attrUpdated` re-materializes the raw style attribute on any attribute change.
 */
export function dropRedundantStyleAttribute(component: Component): void {
  const attributes = component.get('attributes') as Record<string, unknown> | undefined;
  const rawStyle = attributes?.style;
  if (typeof rawStyle !== 'string' || rawStyle === '') {
    return;
  }
  if (Object.keys(component.getStyle()).length === 0) {
    return;
  }
  component.removeAttributes('style');
}

/**
 * Establishes the redundant-style-attribute invariant over the entire loaded component tree in one
 * pass. Run once after a template loads; `component:add` keeps it true for everything added later.
 */
export function normalizeStyleAttributesTree(editor: Editor): void {
  editor.getWrapper()?.onAll(dropRedundantStyleAttribute);
}

/** Clears one style property plus the copies the set path mirrored (divider cell, Outlook bgcolor) — removals emit no styleUpdate, so set-side listeners cannot. */
export function clearComponentStyleProperty(editor: Editor, component: Component, property: string): void {
  component.removeStyle(property);
  clearDividerCompatibilityProperty(editor, {component, property});

  if (property === BACKGROUND_COLOR_STYLE_PROPERTY) {
    removeBgColorAttribute(component);
  }

  editor.trigger(`${STYLE_UPDATE_EVENT_PREFIX}${property}`, component);
}

/** Removes the Outlook bgcolor fallback, dropping the raw style attribute first — attrUpdated would rebuild model.style from it and resurrect the cleared color. */
export function removeBgColorAttribute(component: Component): void {
  const attributes = component.get('attributes') as Record<string, unknown> | undefined;

  if (attributes?.[BGCOLOR_ATTRIBUTE] === undefined) {
    return;
  }

  dropRedundantStyleAttribute(component);
  component.removeAttributes([BGCOLOR_ATTRIBUTE]);
}

export function useStyleChangeSubscription(editor: Editor | null, component: Component, property: string): StyleChangeSubscribe {
  return useCallback(
    (onChange: () => void): (() => void) => {
      if (!editor) {
        return (): void => undefined;
      }
      const styleUpdateEvent = `${STYLE_UPDATE_EVENT_PREFIX}${property}`;
      component.on('change:style', onChange);
      editor.on(styleUpdateEvent, onChange);
      return (): void => {
        component.off('change:style', onChange);
        editor.off(styleUpdateEvent, onChange);
      };
    },
    [editor, component, property]
  );
}

/**
 * Live value of one CSS property; re-renders on `change:style` and the `STYLE_UPDATE_EVENT_PREFIX` channel.
 * Null-safe: returns '' when editor is null (right-panel controls read editor from context, null until init).
 */
export function useStyleProperty(editor: Editor | null, component: Component, property: string): string {
  const subscribe = useStyleChangeSubscription(editor, component, property);
  const getSnapshot = useCallback((): string => (editor ? getComponentStyleProperty(component, property) : ''), [editor, component, property]);
  return useSyncExternalStore(subscribe, getSnapshot);
}

/** Non-null editor variant of `useStyleProperty`; use when the editor is guaranteed non-null. */
export function useComponentStyleProperty(editor: Editor, component: Component, property: string): string {
  return useStyleProperty(editor, component, property);
}

/**
 * React hook — returns the live value of one component *prop* (non-style model attribute,
 * e.g. `pmw-columns-preset`, `pmw-media-type`). Subscribes to `change:<propName>`.
 */
export function useComponentProp(component: Component, propName: string): unknown {
  const subscribe = useCallback(
    (onChange: () => void) => {
      component.on(`change:${propName}`, onChange);
      return (): void => {
        component.off(`change:${propName}`, onChange);
      };
    },
    [component, propName]
  );

  const getSnapshot = useCallback((): unknown => component.get(propName), [component, propName]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * React hook — returns the live value of one HTML attribute on a component. Subscribes to
 * `change:attributes` since GrapesJS batches attribute mutations under that event.
 */
export function useComponentAttribute(component: Component, attributeName: string): string {
  const subscribe = useCallback(
    (onChange: () => void) => {
      component.on('change:attributes', onChange);
      return (): void => {
        component.off('change:attributes', onChange);
      };
    },
    [component]
  );

  const getSnapshot = useCallback((): string => {
    const attrs = component.getAttributes() as Record<string, string | undefined>;
    return attrs[attributeName] ?? '';
  }, [component, attributeName]);

  return useSyncExternalStore(subscribe, getSnapshot);
}

/** The pmw-link-color value for a text component, or undefined when unset. */
export function getTextLinkColor(textComponent: Component): string | undefined {
  const linkColor = textComponent.getStyle()[EMAIL_MAKER_STYLES.LINK_COLOR];
  return typeof linkColor === 'string' ? linkColor : undefined;
}
